import Phaser from 'phaser';
import { useGameStore } from '../store/gameStore';
import { createGame } from '@game/models/Game';
import { COLORS, TYPOGRAPHY, createCard } from '../ui/theme';
import { MultiplayerManager } from '../multiplayer/managers/MultiplayerManager';
import { resolveMultiplayerConfig } from '../multiplayer/services/NetworkSync';

export default class MenuScene extends Phaser.Scene {
  private multiplayerManager: MultiplayerManager;
  private isMultiplayerMode: boolean = true; // Default to multiplayer
  private gameButtonsContainer!: Phaser.GameObjects.Container;
  private isModalOpen: boolean = false; // Prevent multiple modals

  constructor() {
    super({ key: 'MenuScene' });
    const onGameStart = () => {
      this.resetLocalGameStateForMultiplayerStart();
      console.log('MenuScene: onGameStart callback - transitioning to PoolScene');
      this.scene.start('PoolScene');
    };

    // Use the shared multiplayer manager instance so gameplay scenes observe
    // the same session/player state established from the menu flow.
    this.multiplayerManager = MultiplayerManager.getInstance({ onGameStart });
    this.multiplayerManager.updateOptions({ onGameStart });
  }

  private resetLocalGameStateForMultiplayerStart(): void {
    const sessionState = this.multiplayerManager.getSessionState();
    if (!sessionState?.isMultiplayer || !sessionState.sessionId) {
      return;
    }

    const store = useGameStore.getState();
    const localGame = store.game;

    const preservedSettings = localGame?.settings || {
      pocketSize: 'medium' as const,
      showTargetLine: false,
      timerMode: 'off' as const,
      numPlayers: 2,
    };

    const sessionPlayers = [...(sessionState.players || [])]
      .filter(player => typeof player.playerNumber === 'number')
      .sort((a, b) => (a.playerNumber as number) - (b.playerNumber as number));

    const playerNames =
      sessionPlayers.length >= 2
        ? sessionPlayers.map(player => player.displayName || `Player ${player.playerNumber}`)
        : ['Player 1', 'Player 2'];

    const boardSeed = sessionState.gameState?.boardSeed;
    const freshGame = createGame(playerNames, boardSeed);
    freshGame.id = sessionState.sessionId;
    freshGame.phase = 'pool';
    freshGame.currentPlayerId = 1;
    freshGame.turnNumber = 0;
    freshGame.currentRackLetters = [];
    freshGame.cueBallPosition = null;
    freshGame.settings = {
      ...preservedSettings,
      numPlayers: playerNames.length,
    };

    store.setGame(freshGame);
    console.log('[MenuScene] Reset local game state at game_started boundary', {
      sessionId: freshGame.id,
      players: playerNames,
      currentPlayerId: freshGame.currentPlayerId,
      turnNumber: freshGame.turnNumber,
      rackSize: freshGame.currentRackLetters.length,
      cueBallPosition: freshGame.cueBallPosition,
    });
  }

  preload() {
    // Load assets here when we have them
  }

  create() {
    const { width, height } = this.cameras.main;

    // Reset modal state when scene is created/restarted
    this.isModalOpen = false;

    // Background with gradient effect (simulated with overlapping rectangles)
    this.add.rectangle(width / 2, height / 2, width, height, COLORS.background.primary);
    this.add.rectangle(width / 2, height / 4, width, height / 2, COLORS.background.secondary, 0.5);

    // Main card container
    const cardWidth = 500;
    const cardHeight = 600; // Increased height to accommodate all elements
    const cardX = (width - cardWidth) / 2;
    const cardY = (height - cardHeight) / 2;
    
    createCard(this, cardX, cardY, cardWidth, cardHeight, 10);

    // Title with modern styling
    this.add
      .text(width / 2, cardY + 60, 'RollWords', {
        fontSize: TYPOGRAPHY.fontSize['6xl'],
        color: COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setOrigin(0.5)
      .setDepth(11);

    // Accent line under title
    const lineGraphics = this.add.graphics();
    lineGraphics.fillStyle(COLORS.primary.main, 1);
    lineGraphics.fillRoundedRect(width / 2 - 80, cardY + 100, 160, 4, 2);
    lineGraphics.setDepth(11);

    // Subtitle with gradient effect (simulated with shadow)
    this.add
      .text(width / 2, cardY + 135, 'Pool Table Word Game', {
        fontSize: TYPOGRAPHY.fontSize['2xl'],
        color: COLORS.text.secondary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setOrigin(0.5)
      .setDepth(11);

    // Mode Toggle (Single Player / Multiplayer) - more space from subtitle
    const toggleY = cardY + 190;
    this.createModeToggle(width, toggleY);

    // Button dimensions
    const buttonWidth = 280;
    const buttonHeight = 60;

    // Buttons Container
    const buttonsY = cardY + 280;
    this.createGameButtons(width, buttonsY);

    // Settings Button (positioned to avoid overlap)
    const settingsButtonY = cardY + cardHeight - 80;
    this.createSettingsButton(width, settingsButtonY, buttonWidth, buttonHeight);

    // Instructions at bottom (moved up to avoid overlap)
    this.add
      .text(
        width / 2,
        cardY + cardHeight - 140,
        'Sink letters on the pool table, then play words on the board',
        {
          fontSize: TYPOGRAPHY.fontSize.sm,
          color: COLORS.text.disabled,
          fontFamily: TYPOGRAPHY.fontFamily.default,
          align: 'center',
          wordWrap: { width: cardWidth - 40 },
        }
      )
      .setOrigin(0.5)
      .setDepth(11);

    // Version/copyright — reads build timestamp injected by Vite at build time
    const buildVersion = document.documentElement.getAttribute('data-build-time') ?? 'dev';
    this.add
      .text(width / 2, height - 30, buildVersion, {
        fontSize: TYPOGRAPHY.fontSize.xs,
        color: COLORS.text.disabled,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      })
      .setOrigin(0.5);
  }

  private createModeToggle(width: number, y: number) {
    // Mode toggle container
    const toggleWidth = 300;
    const toggleHeight = 50;
    
    // Background
    const toggleBg = this.add.graphics();
    toggleBg.fillStyle(COLORS.background.secondary, 1);
    toggleBg.lineStyle(2, COLORS.primary.main, 0.3);
    toggleBg.fillRoundedRect(width / 2 - toggleWidth / 2, y - toggleHeight / 2, toggleWidth, toggleHeight, 25);
    toggleBg.strokeRoundedRect(width / 2 - toggleWidth / 2, y - toggleHeight / 2, toggleWidth, toggleHeight, 25);
    toggleBg.setDepth(11);

    // Single Player Button
    const singlePlayerBg = this.add.graphics();
    const multiplayerBg = this.add.graphics();
    
    this.updateToggleButtons(singlePlayerBg, multiplayerBg, width, y, toggleWidth, toggleHeight);

    // Single Player text
    const singlePlayerText = this.add.text(width / 2 - toggleWidth / 4, y, 'Single Player', {
      fontSize: TYPOGRAPHY.fontSize.base,
      color: this.isMultiplayerMode ? COLORS.text.secondary : '#ffffff',
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(12);

    // Multiplayer text
    const multiplayerText = this.add.text(width / 2 + toggleWidth / 4, y, 'Multiplayer', {
      fontSize: TYPOGRAPHY.fontSize.base,
      color: this.isMultiplayerMode ? '#ffffff' : COLORS.text.secondary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(12);

    // Single Player interaction
    singlePlayerBg.setInteractive(
      new Phaser.Geom.Rectangle(width / 2 - toggleWidth / 2, y - toggleHeight / 2, toggleWidth / 2, toggleHeight),
      Phaser.Geom.Rectangle.Contains
    );
    singlePlayerBg.on('pointerdown', () => {
      if (this.isMultiplayerMode) {
        this.isMultiplayerMode = false;
        this.updateToggleButtons(singlePlayerBg, multiplayerBg, width, y, toggleWidth, toggleHeight);
        singlePlayerText.setColor('#ffffff');
        multiplayerText.setColor(COLORS.text.secondary);
        this.updateGameButtons();
      }
    });

    // Multiplayer interaction
    multiplayerBg.setInteractive(
      new Phaser.Geom.Rectangle(width / 2, y - toggleHeight / 2, toggleWidth / 2, toggleHeight),
      Phaser.Geom.Rectangle.Contains
    );
    multiplayerBg.on('pointerdown', () => {
      if (!this.isMultiplayerMode) {
        this.isMultiplayerMode = true;
        this.updateToggleButtons(singlePlayerBg, multiplayerBg, width, y, toggleWidth, toggleHeight);
        singlePlayerText.setColor(COLORS.text.secondary);
        multiplayerText.setColor('#ffffff');
        this.updateGameButtons();
      }
    });
  }

  private updateToggleButtons(singlePlayerBg: Phaser.GameObjects.Graphics, multiplayerBg: Phaser.GameObjects.Graphics, width: number, y: number, toggleWidth: number, toggleHeight: number) {
    // Clear both buttons
    singlePlayerBg.clear();
    multiplayerBg.clear();

    if (this.isMultiplayerMode) {
      // Highlight multiplayer (right half) with matching rounded corners
      multiplayerBg.fillStyle(COLORS.primary.main, 1);
      multiplayerBg.fillRoundedRect(
        width / 2 + 2, // Small offset to avoid overlap
        y - toggleHeight / 2 + 2, 
        toggleWidth / 2 - 4, // Slightly smaller to stay within bounds
        toggleHeight - 4,
        { tl: 0, tr: 21, bl: 0, br: 21 } // Round right corners to match background
      );
    } else {
      // Highlight single player (left half) with matching rounded corners
      singlePlayerBg.fillStyle(COLORS.primary.main, 1);
      singlePlayerBg.fillRoundedRect(
        width / 2 - toggleWidth / 2 + 2, // Small offset to avoid overlap
        y - toggleHeight / 2 + 2, 
        toggleWidth / 2 - 4, // Slightly smaller to stay within bounds
        toggleHeight - 4,
        { tl: 21, tr: 0, bl: 21, br: 0 } // Round left corners to match background
      );
    }

    singlePlayerBg.setDepth(11);
    multiplayerBg.setDepth(11);
  }

  private createGameButtons(width: number, y: number) {
    // We'll store references to buttons to update them
    this.gameButtonsContainer = this.add.container(width / 2, y);
    this.gameButtonsContainer.setDepth(11);
    
    this.updateGameButtons();
  }

  private updateGameButtons() {
    // Clear existing buttons
    if (this.gameButtonsContainer) {
      this.gameButtonsContainer.destroy();
    }

    const { width } = this.cameras.main;
    const buttonWidth = 280;
    const buttonHeight = 60;
    const buttonsY = this.cameras.main.height / 2 + 30; // Adjust based on card position

    this.gameButtonsContainer = this.add.container(width / 2, buttonsY);
    this.gameButtonsContainer.setDepth(11);

    if (this.isMultiplayerMode) {
      this.createMultiplayerButtons(buttonWidth, buttonHeight);
    } else {
      this.createSinglePlayerButtons(buttonWidth, buttonHeight);
    }
  }

  private createSinglePlayerButtons(buttonWidth: number, buttonHeight: number) {
    // Single "Start New Game" button
    const button = this.createButton('Start New Game', 0, 0, buttonWidth, buttonHeight, true);
    button.graphics.on('pointerdown', () => {
      this.time.delayedCall(100, () => {
        this.startNewGame();
      });
    });
    this.gameButtonsContainer.add(button.container);
  }

  private createMultiplayerButtons(buttonWidth: number, buttonHeight: number) {
    // Create Game button
    const createButton = this.createButton('Create Game', 0, -40, buttonWidth, buttonHeight, true);
    createButton.graphics.on('pointerdown', () => {
      // Prevent multiple modals
      if (this.isModalOpen) return;
      
      this.time.delayedCall(100, () => {
        this.createMultiplayerGame();
      });
    });

    // Join Game button
    const joinButton = this.createButton('Join Game', 0, 40, buttonWidth, buttonHeight, false);
    joinButton.graphics.on('pointerdown', () => {
      // Prevent multiple modals
      if (this.isModalOpen) return;
      
      this.time.delayedCall(100, () => {
        this.showJoinGameInput();
      });
    });

    this.gameButtonsContainer.add([createButton.container, joinButton.container]);
  }

  private createButton(text: string, x: number, y: number, width: number, height: number, isPrimary: boolean) {
    // Create a container for the button to keep all elements together
    const buttonContainer = this.add.container(x, y);
    buttonContainer.setDepth(11);

    // Button shadow (positioned relative to container)
    const shadow = this.add.graphics();
    shadow.fillStyle(0x000000, 0.3);
    shadow.fillRoundedRect(-width / 2 + 3, -height / 2 + 3, width, height, 12);
    shadow.setDepth(10);
    buttonContainer.add(shadow);

    // Button background (positioned relative to container)
    const graphics = this.add.graphics();
    const fillColor = isPrimary ? COLORS.primary.main : COLORS.background.primary;
    graphics.fillStyle(fillColor, 1);
    if (!isPrimary) {
      graphics.lineStyle(2, COLORS.primary.main, 1);
      graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, 12);
    }
    graphics.fillRoundedRect(-width / 2, -height / 2, width, height, 12);
    graphics.setDepth(11);
    buttonContainer.add(graphics);

    // Button text (centered in container)
    const buttonText = this.add.text(0, 0, text, {
      fontSize: TYPOGRAPHY.fontSize['2xl'],
      color: isPrimary ? '#ffffff' : COLORS.primary.text,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(12);
    buttonContainer.add(buttonText);

    // Interactive area (relative to container)
    const interactiveArea = new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height);
    graphics.setInteractive(interactiveArea, Phaser.Geom.Rectangle.Contains);

    // Hover effects (all relative to container)
    graphics.on('pointerover', () => {
      graphics.clear();
      const hoverColor = isPrimary ? COLORS.primary.light : COLORS.primary.light;
      graphics.fillStyle(hoverColor, isPrimary ? 1 : 0.1);
      if (!isPrimary) {
        graphics.lineStyle(2, COLORS.primary.light, 1);
        graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, 12);
      }
      graphics.fillRoundedRect(-width / 2, -height / 2, width, height, 12);
      this.input.setDefaultCursor('pointer');
    });

    graphics.on('pointerout', () => {
      graphics.clear();
      graphics.fillStyle(fillColor, 1);
      if (!isPrimary) {
        graphics.lineStyle(2, COLORS.primary.main, 1);
        graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, 12);
      }
      graphics.fillRoundedRect(-width / 2, -height / 2, width, height, 12);
      this.input.setDefaultCursor('default');
    });

    graphics.on('pointerdown', () => {
      graphics.clear();
      const pressedColor = isPrimary ? COLORS.primary.dark : COLORS.primary.dark;
      graphics.fillStyle(pressedColor, isPrimary ? 1 : 0.2);
      if (!isPrimary) {
        graphics.lineStyle(2, COLORS.primary.dark, 1);
        graphics.strokeRoundedRect(-width / 2, -height / 2, width, height, 12);
      }
      graphics.fillRoundedRect(-width / 2, -height / 2, width, height, 12);
    });

    return { container: buttonContainer, graphics, text: buttonText, shadow };
  }

  private createSettingsButton(width: number, y: number, buttonWidth: number, buttonHeight: number) {
    const button = this.createButton('Settings', width / 2, y, buttonWidth, buttonHeight, false);
    button.graphics.on('pointerdown', () => {
      this.time.delayedCall(100, () => {
        this.scene.start('SettingsScene');
      });
    });
  }

  // ==================== Multiplayer Actions ====================

  private async createMultiplayerGame() {
    // Prevent multiple modals
    if (this.isModalOpen) return;
    this.showCreateGameInput();
  }

  private showCreateGameInput() {
    // Prevent multiple instances
    if (this.isModalOpen) return;
    this.isModalOpen = true;

    // Create overlay that blocks ALL clicks
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.85); // More opaque
    overlay.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    overlay.setDepth(20);
    overlay.setInteractive();
    
    // Click outside modal = cancel
    overlay.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const modalBounds = {
        x: this.cameras.main.width / 2 - 200,
        y: this.cameras.main.height / 2 - 150,
        width: 400,
        height: 300
      };
      
      if (pointer.x < modalBounds.x || pointer.x > modalBounds.x + modalBounds.width ||
          pointer.y < modalBounds.y || pointer.y > modalBounds.y + modalBounds.height) {
        cleanup();
      }
    });

    // Modal
    const modalWidth = 400;
    const modalHeight = 300;
    const modalX = this.cameras.main.width / 2 - modalWidth / 2;
    const modalY = this.cameras.main.height / 2 - modalHeight / 2;

    const modal = this.add.graphics();
    modal.fillStyle(COLORS.background.primary, 1);
    modal.lineStyle(2, COLORS.primary.main, 1);
    modal.fillRoundedRect(modalX, modalY, modalWidth, modalHeight, 12);
    modal.strokeRoundedRect(modalX, modalY, modalWidth, modalHeight, 12);
    modal.setDepth(21);
    modal.setInteractive(); // Block clicks from going through

    // Title
    const title = this.add.text(this.cameras.main.width / 2, modalY + 50, 'Create Game', {
      fontSize: TYPOGRAPHY.fontSize['3xl'],
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Instructions label - align with input field center 
    const instructions = this.add.text(modalX + 200, modalY + 100, 'Enter your name:', {
      fontSize: TYPOGRAPHY.fontSize.lg,
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Name input background - positioned BELOW the label
    const nameInputBg = this.add.graphics();
    nameInputBg.fillStyle(COLORS.background.secondary, 1);
    nameInputBg.lineStyle(2, COLORS.primary.main, 0.7);
    nameInputBg.fillRoundedRect(modalX + 50, modalY + 125, 300, 40, 8);
    nameInputBg.strokeRoundedRect(modalX + 50, modalY + 125, 300, 40, 8);
    nameInputBg.setDepth(21);

    // Create button
    const createButton = this.createButton('Create Game', this.cameras.main.width / 2, modalY + 200, 200, 45, true);
    createButton.container.setDepth(23);
    
    // Cancel button
    const cancelButton = this.createButton('Cancel', this.cameras.main.width / 2, modalY + 250, 200, 40, false);
    cancelButton.container.setDepth(23);

    // HTML input for name - positioned to exactly match the background
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Enter your name';
    nameInput.maxLength = 20;
    nameInput.style.position = 'absolute';
    
    // Function to update input position
    const updateInputPosition = () => {
      const gameCanvas = document.querySelector('canvas');
      if (!gameCanvas) return;
      
      const canvasRect = gameCanvas.getBoundingClientRect();
      const scaleX = canvasRect.width / this.cameras.main.width;
      const scaleY = canvasRect.height / this.cameras.main.height;
      
      // Convert Phaser coordinates to screen coordinates
      const phaserModalX = this.cameras.main.width / 2 - modalWidth / 2;
      const phaserModalY = this.cameras.main.height / 2 - modalHeight / 2;
      const phaserInputX = phaserModalX + 50;
      const phaserInputY = phaserModalY + 125;
      
      // Transform to actual screen position
      const screenX = canvasRect.left + (phaserInputX * scaleX);
      const screenY = canvasRect.top + (phaserInputY * scaleY);
      
      nameInput.style.left = screenX + 'px';
      nameInput.style.top = screenY + 'px';
      nameInput.style.width = (300 * scaleX) + 'px';
      nameInput.style.height = (40 * scaleY) + 'px';
    };
    
    // Initial positioning
    updateInputPosition();
    
    // Style input (size handled by updateInputPosition)
    nameInput.style.zIndex = '1001';
    nameInput.style.padding = '12px 15px';
    nameInput.style.fontSize = '16px';
    nameInput.style.border = 'none';
    nameInput.style.borderRadius = '8px';
    nameInput.style.backgroundColor = 'transparent';
    nameInput.style.color = '#ffffff';
    nameInput.style.outline = 'none';
    nameInput.style.boxSizing = 'border-box';
    nameInput.style.lineHeight = '16px';
    nameInput.style.fontFamily = 'Arial, sans-serif';
    
    // Add resize listener to update position
    const resizeListener = () => updateInputPosition();
    window.addEventListener('resize', resizeListener);
    
    document.body.appendChild(nameInput);
    nameInput.focus();

    const cleanup = () => {
      this.isModalOpen = false;
      window.removeEventListener('resize', resizeListener);
      if (document.body.contains(nameInput)) document.body.removeChild(nameInput);
      overlay.destroy();
      modal.destroy();
      title.destroy();
      instructions.destroy();
      nameInputBg.destroy();
      createButton.container.destroy();
      cancelButton.container.destroy();
    };

    createButton.graphics.on('pointerdown', () => {
      const playerName = nameInput.value.trim();
      if (playerName.length > 0) {
        cleanup();
        this.doCreateMultiplayerGame(playerName);
      } else {
        nameInput.style.border = '2px solid #ef4444';
      }
    });

    cancelButton.graphics.on('pointerdown', () => {
      cleanup();
    });

    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const playerName = nameInput.value.trim();
        if (playerName.length > 0) {
          cleanup();
          this.doCreateMultiplayerGame(playerName);
        }
      }
    });
  }

  private async doCreateMultiplayerGame(playerName: string) {
    try {
      const endpointCheck = this.validateCreateSessionEndpoint();
      if (!endpointCheck.valid) {
        this.showMultiplayerUnavailableFallback(endpointCheck.message);
        return;
      }

      console.log(`${playerName} creating multiplayer game...`);
      console.log(`[MenuScene] Using multiplayer endpoint: ${endpointCheck.endpoint}`);
      await this.multiplayerManager.createSession({ displayName: playerName, maxPlayers: 4 });
      
      const sessionState = this.multiplayerManager.getSessionState();
      if (sessionState.shareCode) {
        console.log(`Game created! Share code: ${sessionState.shareCode}`);
        this.showShareCodeDisplay(sessionState.shareCode);
      }
    } catch (error) {
      console.error('Failed to create multiplayer game:', error);
      this.showMultiplayerUnavailableFallback('Multiplayer is currently unavailable. Please try again or continue in single-player mode.');
    }
  }

  private validateCreateSessionEndpoint(): { valid: boolean; message: string; endpoint?: string } {
    try {
      const config = resolveMultiplayerConfig();
      const endpoint = new URL(config.serverUrl);
      const isBrowserHttps = window.location.protocol === 'https:';
      const isLocalhost = endpoint.hostname === 'localhost' || endpoint.hostname === '127.0.0.1';

      // If frontend is served over HTTPS, backend cannot be localhost.
      if (isBrowserHttps && isLocalhost) {
        return {
          valid: false,
          message:
            'Multiplayer backend is still set to localhost. ' +
            'Set VITE_MULTIPLAYER_SERVER_URL to your production backend URL.'
        };
      }

      return {
        valid: true,
        message: 'OK',
        endpoint: endpoint.toString()
      };
    } catch (error) {
      return {
        valid: false,
        message: 'Invalid multiplayer backend URL configuration.'
      };
    }
  }

  private showShareCodeDisplay(shareCode: string) {
    // Show the game lobby instead of just the share code
    this.showGameLobby(shareCode);
  }

  private showGameLobby(shareCode: string) {
    // Create overlay for the game lobby
    if (this.isModalOpen) return;
    this.isModalOpen = true;

    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.85);
    overlay.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    overlay.setDepth(20);
    // Do NOT setInteractive on the full-screen overlay — it would swallow clicks on
    // scene buttons (e.g. Settings) that sit outside the modal area. The modal itself
    // calls setInteractive() to block clicks within its bounds.

    // Modal
    const modalWidth = 500;
    const modalHeight = 500;
    const modalX = this.cameras.main.width / 2 - modalWidth / 2;
    const modalY = this.cameras.main.height / 2 - modalHeight / 2;

    const modal = this.add.graphics();
    modal.fillStyle(COLORS.background.primary, 1);
    modal.lineStyle(2, COLORS.primary.main, 1);
    modal.fillRoundedRect(modalX, modalY, modalWidth, modalHeight, 12);
    modal.strokeRoundedRect(modalX, modalY, modalWidth, modalHeight, 12);
    modal.setDepth(21);
    modal.setInteractive();

    // Title
    const title = this.add.text(this.cameras.main.width / 2, modalY + 40, 'Game Lobby', {
      fontSize: TYPOGRAPHY.fontSize['3xl'],
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Status message
    const statusText = this.add.text(this.cameras.main.width / 2, modalY + 80, 'Waiting for players to join...', {
      fontSize: TYPOGRAPHY.fontSize.base,
      color: COLORS.text.secondary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Share code section
    const shareCodeLabel = this.add.text(this.cameras.main.width / 2, modalY + 120, 'Share Code:', {
      fontSize: TYPOGRAPHY.fontSize.lg,
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Share code display with background
    const shareCodeBg = this.add.graphics();
    shareCodeBg.fillStyle(COLORS.primary.main, 0.2);
    shareCodeBg.lineStyle(2, 0x3b82f6, 1);
    shareCodeBg.fillRoundedRect(modalX + 150, modalY + 140, 200, 50, 8);
    shareCodeBg.strokeRoundedRect(modalX + 150, modalY + 140, 200, 50, 8);
    shareCodeBg.setDepth(21);

    const shareCodeDisplay = this.add.text(this.cameras.main.width / 2, modalY + 165, shareCode, {
      fontSize: TYPOGRAPHY.fontSize['3xl'],
      color: COLORS.primary.text,
      fontFamily: TYPOGRAPHY.fontFamily.default,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(22);

    const instructions = this.add.text(this.cameras.main.width / 2, modalY + 210, 'Share this code with friends to let them join your game', {
      fontSize: TYPOGRAPHY.fontSize.base,
      color: COLORS.text.secondary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Player count section - will be updated dynamically
    const playersLabel = this.add.text(this.cameras.main.width / 2, modalY + 250, 'Players 1/4', {
      fontSize: TYPOGRAPHY.fontSize.lg,
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Player list area
    const playerBg = this.add.graphics();
    playerBg.fillStyle(COLORS.background.secondary, 1);
    playerBg.lineStyle(1, COLORS.primary.main, 0.3);
    playerBg.fillRoundedRect(modalX + 50, modalY + 280, 400, 80, 8);
    playerBg.strokeRoundedRect(modalX + 50, modalY + 280, 400, 80, 8);
    playerBg.setDepth(21);

    // Determine if this player is the host or a joiner
    const sessionState = this.multiplayerManager.getSessionState();
    const isHost = sessionState.isCreator || false;
    const currentPlayerName = sessionState.players[0]?.displayName || 'You';
    
    const playerText = this.add.text(modalX + 70, modalY + 290, 
      isHost ? `1    ${currentPlayerName} (Host) 👑` : `1    ${currentPlayerName}`, {
      fontSize: TYPOGRAPHY.fontSize.base,
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setDepth(22);

    const connectionStatus = this.add.text(modalX + 70, modalY + 320, '🟢 Connected', {
      fontSize: TYPOGRAPHY.fontSize.sm,
      color: '#10b981',
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setDepth(22);

    // Game settings info
    const settingsText = this.add.text(this.cameras.main.width / 2, modalY + 380, 'Max Players: 4    Turn Time: 2 minutes    Bonus Letters: Enabled', {
      fontSize: TYPOGRAPHY.fontSize.sm,
      color: COLORS.text.secondary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Buttons - only show start button for hosts
    let startButton: ReturnType<typeof this.createButton> | undefined;
    if (isHost) {
      startButton = this.createButton('Need 2+ Players', this.cameras.main.width / 2 - 110, modalY + 430, 200, 45, false);
      startButton.container.setDepth(22);
      // Disable the button initially
      startButton.graphics.setAlpha(0.5);
      startButton.text.setColor('#666666');
    }

    const leaveButton = this.createButton('Leave Game', isHost ? this.cameras.main.width / 2 + 110 : this.cameras.main.width / 2, modalY + 430, 160, 45, false);
    leaveButton.container.setDepth(22);
    // Style the button as red using graphics directly
    leaveButton.graphics.clear();
    leaveButton.graphics.fillStyle(0xff6b6b, 1); // Red background
    leaveButton.graphics.fillRoundedRect(-80, -22.5, 160, 45, 12);

    const updateLobbyData = () => {
      const sessionState = this.multiplayerManager.getSessionState();
      console.log('Updating lobby data:', sessionState);
      
      if (sessionState.players) {
        const playerCount = sessionState.players.length;
        playersLabel.setText(`Players ${playerCount}/4`);
        
        // Update player list display
        let playerListText = '';
        sessionState.players.forEach((player: any, index: number) => {
          const hostIndicator = (index === 0 && sessionState.isCreator) ? ' (Host) 👑' : '';
          playerListText += `${index + 1}    ${player.displayName}${hostIndicator}\n`;
        });
        playerText.setText(playerListText.trim());
        
        // Enable start button if enough players and user is host
        if (startButton && playerCount >= 2) {
          startButton.text.setText('Start Game');
          startButton.graphics.setAlpha(1);
          startButton.text.setColor(COLORS.text.primary);
          startButton.graphics.removeAllListeners('pointerdown');
          startButton.graphics.on('pointerdown', () => {
            this.startMultiplayerGame();
            cleanup();
          });
        }
      }
    };

    const cleanup = () => {
      this.isModalOpen = false;
      
      // Unsubscribe from socket events
      if (joinedSub) joinedSub.unsubscribe();
      if (leftSub) leftSub.unsubscribe();
      
      overlay.destroy();
      modal.destroy();
      title.destroy();
      statusText.destroy();
      shareCodeLabel.destroy();
      shareCodeBg.destroy();
      shareCodeDisplay.destroy();
      instructions.destroy();
      playersLabel.destroy();
      playerBg.destroy();
      playerText.destroy();
      connectionStatus.destroy();
      settingsText.destroy();
      if (startButton) startButton.container.destroy();
      leaveButton.container.destroy();
    };
    
    // Initial update
    updateLobbyData();
    
    // Set up real-time event listeners for lobby updates
    const networkSync = this.multiplayerManager.getNetworkSync();
    
    const onPlayerJoined = () => {
      console.log('[MenuScene] Player joined - updating lobby display');
      updateLobbyData();
    };
    
    const onPlayerLeft = () => {
      console.log('[MenuScene] Player left - updating lobby display');  
      updateLobbyData();
    };
    
    // Subscribe to real-time events
    const joinedSub = networkSync.on('player_joined', onPlayerJoined);
    const leftSub = networkSync.on('player_left', onPlayerLeft);
    
    leaveButton.graphics.on('pointerdown', () => {
      cleanup();
      // TODO: Leave the session and disconnect from multiplayer
      this.multiplayerManager.leaveSession();
    });
  }

  private showMultiplayerUnavailableFallback(message: string) {
    console.warn('[MenuScene] Multiplayer unavailable fallback shown:', message);

    const prompt = `${message}\n\nSwitch to Single Player?`;
    this.showErrorMessage(prompt);

    this.time.delayedCall(600, () => {
      this.isMultiplayerMode = false;
      this.updateGameButtons();
    });
  }

  private showErrorMessage(message: string) {
    // Simple error display
    console.error(message);
    
    // Create a temporary error overlay
    const errorOverlay = this.add.graphics();
    errorOverlay.fillStyle(0x000000, 0.8);
    errorOverlay.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    errorOverlay.setDepth(30);
    errorOverlay.setInteractive();

    const errorBg = this.add.graphics();
    errorBg.fillStyle(0x991b1b, 1); // Red background
    errorBg.lineStyle(2, 0xef4444, 1);
    const bgWidth = 400;
    const bgHeight = 200;
    const bgX = this.cameras.main.width / 2 - bgWidth / 2;
    const bgY = this.cameras.main.height / 2 - bgHeight / 2;
    errorBg.fillRoundedRect(bgX, bgY, bgWidth, bgHeight, 12);
    errorBg.strokeRoundedRect(bgX, bgY, bgWidth, bgHeight, 12);
    errorBg.setDepth(31);

    const errorTitle = this.add.text(this.cameras.main.width / 2, bgY + 50, 'Error', {
      fontSize: TYPOGRAPHY.fontSize['2xl'],
      color: '#ffffff',
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(32);

    const errorText = this.add.text(this.cameras.main.width / 2, bgY + 100, message, {
      fontSize: TYPOGRAPHY.fontSize.base,
      color: '#ffffff',
      fontFamily: TYPOGRAPHY.fontFamily.default,
      wordWrap: { width: 350 },
    }).setOrigin(0.5).setDepth(32);

    const okButton = this.createButton('OK', this.cameras.main.width / 2, bgY + 150, 100, 40, true);
    okButton.container.setDepth(32);

    const cleanupError = () => {
      errorOverlay.destroy();
      errorBg.destroy();
      errorTitle.destroy();
      errorText.destroy();
      okButton.container.destroy();
    };

    okButton.graphics.on('pointerdown', cleanupError);
    errorOverlay.on('pointerdown', cleanupError);

    // Auto-dismiss after 5 seconds
    this.time.delayedCall(5000, cleanupError);
  }

  private showJoinGameInput() {
    // Prevent multiple instances
    if (this.isModalOpen) return;
    this.isModalOpen = true;

    // Create overlay that blocks ALL clicks
    const overlay = this.add.graphics();
    overlay.fillStyle(0x000000, 0.85); // More opaque
    overlay.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    overlay.setDepth(20);
    overlay.setInteractive();
    
    // Click outside modal = cancel
    overlay.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const modalBounds = {
        x: this.cameras.main.width / 2 - 225,
        y: this.cameras.main.height / 2 - 200,
        width: 450,
        height: 400
      };
      
      if (pointer.x < modalBounds.x || pointer.x > modalBounds.x + modalBounds.width ||
          pointer.y < modalBounds.y || pointer.y > modalBounds.y + modalBounds.height) {
        cleanup();
      }
    });

    // Modal
    const modalWidth = 450;
    const modalHeight = 400;
    const modalX = this.cameras.main.width / 2 - modalWidth / 2;
    const modalY = this.cameras.main.height / 2 - modalHeight / 2;

    const modal = this.add.graphics();
    modal.fillStyle(COLORS.background.primary, 1);
    modal.lineStyle(2, COLORS.primary.main, 1);
    modal.fillRoundedRect(modalX, modalY, modalWidth, modalHeight, 12);
    modal.strokeRoundedRect(modalX, modalY, modalWidth, modalHeight, 12);
    modal.setDepth(21);
    modal.setInteractive(); // Block clicks from going through

    // Title
    const title = this.add.text(this.cameras.main.width / 2, modalY + 40, 'Join Game', {
      fontSize: TYPOGRAPHY.fontSize['3xl'],
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Player name section - align with input field center
    const nameLabel = this.add.text(modalX + 225, modalY + 90, 'Your Name:', {
      fontSize: TYPOGRAPHY.fontSize.lg,
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Name input background
    const nameInputBg = this.add.graphics();
    nameInputBg.fillStyle(COLORS.background.secondary, 1);
    nameInputBg.lineStyle(2, COLORS.primary.main, 0.7);
    nameInputBg.fillRoundedRect(modalX + 75, modalY + 110, 300, 45, 8);
    nameInputBg.strokeRoundedRect(modalX + 75, modalY + 110, 300, 45, 8);
    nameInputBg.setDepth(21);

    // Share code section - align with input field center
    const codeLabel = this.add.text(modalX + 225, modalY + 180, 'Game Share Code:', {
      fontSize: TYPOGRAPHY.fontSize.lg,
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(22);

    // Code input background with clear visual indication
    const codeInputBg = this.add.graphics();
    codeInputBg.fillStyle(COLORS.primary.main, 0.1);
    codeInputBg.lineStyle(3, COLORS.primary.main, 1);
    codeInputBg.fillRoundedRect(modalX + 125, modalY + 200, 200, 45, 8);
    codeInputBg.strokeRoundedRect(modalX + 125, modalY + 200, 200, 45, 8);
    codeInputBg.setDepth(21);

    // No placeholder text needed - we have a clear label

    // Join button
    const joinButton = this.createButton('Join Game', this.cameras.main.width / 2, modalY + 290, 200, 45, true);
    joinButton.container.setDepth(22);
    
    // Cancel button
    const cancelButton = this.createButton('Cancel', this.cameras.main.width / 2, modalY + 350, 200, 40, false);
    cancelButton.container.setDepth(22);

    // Create HTML inputs with proper positioning

    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Enter your name';
    nameInput.maxLength = 20;
    nameInput.style.position = 'absolute';

    const codeInput = document.createElement('input');
    codeInput.type = 'text';
    codeInput.placeholder = 'ABCD';
    codeInput.maxLength = 4;
    codeInput.style.position = 'absolute';
    
    // Function to update input positions
    const updateInputPositions = () => {
      const gameCanvas = document.querySelector('canvas');
      if (!gameCanvas) return;
      
      const canvasRect = gameCanvas.getBoundingClientRect();
      const scaleX = canvasRect.width / this.cameras.main.width;
      const scaleY = canvasRect.height / this.cameras.main.height;
      
      // Convert Phaser coordinates to screen coordinates for name input
      const phaserModalX = this.cameras.main.width / 2 - modalWidth / 2;
      const phaserModalY = this.cameras.main.height / 2 - modalHeight / 2;
      const phaserNameInputX = phaserModalX + 75;
      const phaserNameInputY = phaserModalY + 110;
      const phaserCodeInputX = phaserModalX + 125;
      const phaserCodeInputY = phaserModalY + 200;
      
      // Transform to actual screen positions
      const nameScreenX = canvasRect.left + (phaserNameInputX * scaleX);
      const nameScreenY = canvasRect.top + (phaserNameInputY * scaleY);
      const codeScreenX = canvasRect.left + (phaserCodeInputX * scaleX);
      const codeScreenY = canvasRect.top + (phaserCodeInputY * scaleY);
      
      // Update name input
      nameInput.style.left = nameScreenX + 'px';
      nameInput.style.top = nameScreenY + 'px';
      nameInput.style.width = (300 * scaleX) + 'px';
      nameInput.style.height = (45 * scaleY) + 'px';
      
      // Update code input
      codeInput.style.left = codeScreenX + 'px';
      codeInput.style.top = codeScreenY + 'px';
      codeInput.style.width = (200 * scaleX) + 'px';
      codeInput.style.height = (45 * scaleY) + 'px';
    };
    
    // Set initial positions
    updateInputPositions();
    
    // Style name input (size handled by updateInputPositions)
    nameInput.style.zIndex = '1001';
    nameInput.style.padding = '12px 15px';
    nameInput.style.fontSize = '16px';
    nameInput.style.border = 'none';
    nameInput.style.borderRadius = '8px';
    nameInput.style.backgroundColor = 'transparent';
    nameInput.style.color = '#ffffff';
    nameInput.style.outline = 'none';
    nameInput.style.boxSizing = 'border-box';

    // Style code input (size handled by updateInputPositions)
    codeInput.style.zIndex = '1001';
    codeInput.style.padding = '12px 15px';
    codeInput.style.fontSize = '20px';
    codeInput.style.fontWeight = 'bold';
    codeInput.style.textAlign = 'center';
    codeInput.style.textTransform = 'uppercase';
    codeInput.style.letterSpacing = '4px';
    codeInput.style.border = 'none';
    codeInput.style.borderRadius = '8px';
    codeInput.style.backgroundColor = 'transparent';
    codeInput.style.color = '#ffffff';
    codeInput.style.outline = 'none';
    codeInput.style.boxSizing = 'border-box';
    
    // Add resize listener
    const resizeListener = () => updateInputPositions();
    window.addEventListener('resize', resizeListener);
    
    document.body.appendChild(nameInput);
    document.body.appendChild(codeInput);
    nameInput.focus();

    // No placeholder text event handlers needed since we removed the placeholder

    const cleanup = () => {
      this.isModalOpen = false;
      window.removeEventListener('resize', resizeListener);
      if (document.body.contains(nameInput)) document.body.removeChild(nameInput);
      if (document.body.contains(codeInput)) document.body.removeChild(codeInput);
      overlay.destroy();
      modal.destroy();
      title.destroy();
      nameLabel.destroy();
      nameInputBg.destroy();
      codeLabel.destroy();
      codeInputBg.destroy();
      joinButton.container.destroy();
      cancelButton.container.destroy();
    };

    joinButton.graphics.on('pointerdown', () => {
      const playerName = nameInput.value.trim();
      const shareCode = codeInput.value.trim().toUpperCase();
      console.log('Join button clicked - Name:', playerName, 'Code:', shareCode);
      
      if (playerName.length > 0 && shareCode.length === 4) {
        cleanup();
        this.joinMultiplayerGame(shareCode, playerName);
      } else {
        // Show validation message
        if (playerName.length === 0) nameInput.style.border = '2px solid #ef4444';
        if (shareCode.length !== 4) codeInput.style.border = '2px solid #ef4444';
      }
    });

    cancelButton.graphics.on('pointerdown', () => {
      cleanup();
    });

    // Handle Enter key
    const handleEnter = () => {
      const playerName = nameInput.value.trim();
      const shareCode = codeInput.value.trim().toUpperCase();
      if (playerName.length > 0 && shareCode.length === 4) {
        cleanup();
        this.joinMultiplayerGame(shareCode, playerName);
      }
    };

    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        codeInput.focus();
      }
    });

    codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        handleEnter();
      }
    });
  }

  private async joinMultiplayerGame(shareCode: string, playerName: string) {
    try {
      const endpointCheck = this.validateJoinSessionEndpoint();
      if (!endpointCheck.valid) {
        this.showMultiplayerUnavailableFallback(endpointCheck.message);
        return;
      }

      console.log(`${playerName} joining game with code: ${shareCode}`);
      console.log(`[MenuScene] Using multiplayer endpoint: ${endpointCheck.endpoint}`);
      
      if (!shareCode || shareCode.length !== 4) {
        throw new Error('Invalid share code');
      }
      
      await this.multiplayerManager.joinSession({ shareCode, displayName: playerName });
      
      const sessionState = this.multiplayerManager.getSessionState();
      console.log('Successfully joined game:', sessionState);
      // Show lobby for joined players too
      this.showGameLobby(shareCode);
    } catch (error) {
      console.error('Failed to join game:', error);
      this.showMultiplayerUnavailableFallback(
        'Unable to join multiplayer right now. Verify your share code, or continue in single-player mode.'
      );
    }
  }

  private validateJoinSessionEndpoint(): { valid: boolean; message: string; endpoint?: string } {
    try {
      const config = resolveMultiplayerConfig();
      const endpoint = new URL(config.serverUrl);
      const isBrowserHttps = window.location.protocol === 'https:';
      const isLocalhost = endpoint.hostname === 'localhost' || endpoint.hostname === '127.0.0.1';

      if (isBrowserHttps && isLocalhost) {
        return {
          valid: false,
          message:
            'Multiplayer backend is still set to localhost. ' +
            'Set VITE_MULTIPLAYER_SERVER_URL to your production backend URL.'
        };
      }

      return {
        valid: true,
        message: 'OK',
        endpoint: endpoint.toString()
      };
    } catch (error) {
      return {
        valid: false,
        message: 'Invalid multiplayer backend URL configuration.'
      };
    }
  }

  private startMultiplayerGame() {
    console.log('Starting multiplayer game...');
    const manager = MultiplayerManager.getInstance();
    manager.startGame();
    // Scene transition is handled by the onGameStart callback registered during
    // session setup; do not call this.scene.start directly here.
  }

  private startNewGame() {
    // Initialize game via Zustand store (uses numPl1ayers from settings)
    const { startNewGame } = useGameStore.getState();
    startNewGame(); // Will create players based on settings.numPlayers

    // Transition to PoolScene
    console.log('Starting new game...');
    this.scene.start('PoolScene');
  }

  update() {
    // Update logic here if needed
  }
}
